<script setup lang="ts">
import { inject, onMounted, ref, useTemplateRef, watch } from 'vue'
import { RunInputContext } from '@/services/RunInputService';
import { SavedFilmsService, UploadedSavedFilmDto } from '@/api';
import { ToastInterface } from 'vue-toastification';


const props = defineProps<{
    context: RunInputContext,
    horizontal?: boolean
}>();

const emit = defineEmits<{
    change: [run: RunInputContext]
}>();

const toast = inject<ToastInterface>("toast");

const uploadedFilms = ref<UploadedSavedFilmDto[]>([]);

const fileInput = useTemplateRef('fileInput');


const moviePaths = {
    "4a103000-0000-3000-8000-000000000000": "Halo3",
    "4a100d57-0000-3000-8000-000000000000": "Halo3ODST",
    "4a102eac-0000-3000-8000-000000000000": "HaloReach",
    "4a104000-0000-3000-8000-000000000000": "Halo4",
}

onMounted(() => {
    
})

function onChange() {
    emit("change", props.context);
}

watch(props.context.savedFilmAttachmentIds, async ids => {
    if(!ids) {
        uploadedFilms.value = [];
    }

    for(let id of ids) {
        if(uploadedFilms.value.findIndex(f => f.Id == id) == -1) {
            let resp = await SavedFilmsService.getSavedFilmInfo(id);
            uploadedFilms.value.push(resp);
        }
    }
}, { deep: true, immediate: true });

async function handleUpload(e: Event) {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    const data = new FormData();

    data.append("Filename", file.name);
    data.append("File", file);

    try {
        const resp = await SavedFilmsService.uploadSavedFilm(data);

        if(resp && resp.Id) {
            props.context.savedFilmAttachmentIds.push(resp.Id);
            uploadedFilms.value.push(resp);
            onChange();
        }
    } catch (ex) {
        toast!.error("Failed to upload film. If this is a valid MCC saved film, please contact the tech team to support your film properly");
    }
}

async function removeFilm(film: UploadedSavedFilmDto) {
    const loc = uploadedFilms.value.findIndex(f => f.Id == film.Id);
    uploadedFilms.value.splice(loc, 1)

    const attLoc = props.context.savedFilmAttachmentIds.indexOf(film.Id!);
    props.context.savedFilmAttachmentIds.splice(attLoc, 1);

    onChange();
}

function inputClick(ev: MouseEvent) {
    let gameDir = moviePaths[props.context.gameId];
    let path = `%AppData%\\..\\LocalLow\\MCC\\Temporary\\UserContent\\${gameDir}\\Movie`;

    try {    
        toast!.info(`Copying ${path} to clipboard, paste in the file dialog`)
        setTimeout(() => { 
            navigator.clipboard.writeText(path);
            fileInput.value?.click();
        }, 100);
    } catch {

    }
}

</script>

<template>
    <div>
        <div class="warning-text" v-if="context.requiresSavedFilm && uploadedFilms.length == 0" :style="horizontal ? 'text-align: right' : ''">A Theater File is required for this submission</div>
        <div class="uploader-root" :style="horizontal ? 'flex-direction: row-reverse; align-items: stretch;' : 'flex-direction: column'">
            <div class="film-list" v-if="uploadedFilms.length" :style="horizontal ? 'flex-direction: row;' : ''">
                <div v-if="!horizontal">Uploaded Films:</div>
                <div v-for="film in uploadedFilms" class="film-entry">
                    <button type="button" class="button ghost is-primary" @click="() => removeFilm(film)">X</button>
                    <div class="title">{{film.Game}}(v{{film.Version}}) - {{film.Scenario}}</div>
                    <div class="details">Created at {{film.FilmTimestamp}}</div>
                </div>
            </div>
            <button class="button upload-button" :style="horizontal ? 'height: unset;' : 'width: 100%;'" @click="inputClick" v-if="moviePaths[context.gameId] || context.requiresSavedFilm">
                Upload Film / Theater File
            </button>
            <input type="file" @change="e => handleUpload(e)" ref="fileInput" style="display:none;" />
        </div>
    </div>
</template>

<style scoped lang="scss">
@use "@/assets/haloruns_vars.scss" as *;

.uploader-root {
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-x: auto;
    padding-top: 4px;
    padding-bottom: 4px;
}

.warning-text {
    color: $primary;
}

.upload-button {
    color: $primary;
    border-color: $primary;
    background-color: $body-background-color;
}

.film-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .film-entry {
        border: 1px solid $border-color;
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        gap: 10px;

        .button { 
            grid-row: 1 / span 2;
            height: 100%;
        }

        .title {
            grid-column-start: 2;
            grid-row-start: 1;
            font-size: 1.1rem;
            padding-top: 10px;
            padding-right: 10px;
            margin-bottom: 0;
        }

        
        .details {
            grid-column-start: 2;
            grid-row-start: 2;
            padding-bottom: 10px;
        }
    }
}
</style>
